import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import styles from './RoutesProgressDetailsPage.module.css';
import TimelineBlock from './components/TimelineBlock';
import RoutesNestedTable from './components/RoutesNestedTable/RoutesNestedTable';
// ===== helpers =====
const toLocalDateStr = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const addDays = (date, n) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
};
const startOfWeek = (d) => {
  const day = d.getDay(); // 0-вс
  const diff = (day === 0 ? -6 : 1) - day; // до понедельника
  return addDays(d, diff);
};
const endOfWeek = (d) => addDays(startOfWeek(d), 6);

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
const fmtDateTime = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d)
    ? d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })
    : '—';
};
const fmtTime = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d) ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';
};

const getRouteProgress = (receipts = []) => {
  const total = receipts.length;
  const delivered = receipts.filter(r => r?.status === 'Закрыта').length;
  const opened = receipts.filter(r => r?.status === 'Открыта').length;
  const undelivered = receipts.filter(r => r?.status === 'Не доставлено').length;
  return { total, delivered, opened, undelivered };
};

// статусные флаги на уровень маршрута
const isNotDeparted = (r) => {
  const statuses = (r.receipts || []).map(x => x.status);
  return statuses.length > 0 && statuses.every(s => s === 'Открыта');
};
const isNotFullyComplete = (r) => {
  const receipts = Array.isArray(r.receipts) ? r.receipts : [];
  if (!receipts.length) return false; // нечего оценивать
  const opened = receipts.filter(x => x?.status === 'Открыта').length;
  return opened > 0 && opened < receipts.length; // есть открытые, но не все
};


export default function RoutesProgressDetailsPage() {
  // ===== даты (дефолт: сегодня) =====
  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // ===== данные =====
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===== фильтры =====
  const [personName, setPersonName] = useState('');
  const [personRole, setPersonRole] = useState('all'); // 'driver' | 'loader' | 'all'
  const [selectedRouteIds, setSelectedRouteIds] = useState([]);   // пусто = все
  const [onlyNotDeparted, setOnlyNotDeparted] = useState(false);
  const [onlyNotFullyComplete, setOnlyNotFullyComplete] = useState(false);

  // раскрытие групп в таблице
  const [expandedRoutes, setExpandedRoutes] = useState(() => new Set());

  // ===== шторка =====
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('routes'); // 'routes' | 'person'
  const [drawerSearch, setDrawerSearch] = useState('');
  const touchStartY = useRef(null);

  // ===== загрузка по диапазону =====
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await api.get('/routes/by-date', {
          params: {
            startDate: toLocalDateStr(startDate),
            endDate: toLocalDateStr(endDate),
          }
        });
        if (mounted && Array.isArray(data)) {
          setRoutes(data);
          // актуализируем выбор и раскрытие
          setSelectedRouteIds(prev => prev.filter(id => data.some(r => r.id === id)));
          setExpandedRoutes(new Set()); // всё свернуто по умолчанию
        }
      } catch (e) {
        console.error('Ошибка при загрузке маршрутов:', e);
        if (mounted) { setRoutes([]); setSelectedRouteIds([]); setExpandedRoutes(new Set()); }
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [startDate, endDate]);

  // люди (для даталиста)
  const people = useMemo(
    () => uniq(routes.flatMap(r => [r.driver, r.loader1, r.loader2])),
    [routes]
  );

  // базовая фильтрация (человек/статусы)
  const baseFilteredRoutes = useMemo(() => {
    let list = routes;

    if (personName) {
      list = list.filter(r => {
        const asDriver = r.driver === personName;
        const asLoader = [r.loader1, r.loader2].includes(personName);
        if (personRole === 'driver') return asDriver;
        if (personRole === 'loader') return asLoader;
        return asDriver || asLoader;
      });
    }

    if (onlyNotDeparted) list = list.filter(isNotDeparted);
    if (onlyNotFullyComplete) list = list.filter(isNotFullyComplete);

    return list;
  }, [routes, personName, personRole, onlyNotDeparted, onlyNotFullyComplete]);

  // итоговые маршруты (если есть конкретный выбор)
  const effectiveRoutes = useMemo(() => {
    if (!selectedRouteIds.length) return baseFilteredRoutes;
    const set = new Set(selectedRouteIds);
    return baseFilteredRoutes.filter(r => set.has(r.id));
  }, [baseFilteredRoutes, selectedRouteIds]);

  const effectiveReceipts = useMemo(
    () => effectiveRoutes.flatMap(r => r.receipts || []),
    [effectiveRoutes]
  );

  const aggProgress = useMemo(() => getRouteProgress(effectiveReceipts), [effectiveReceipts]);

  // действия
  const openDrawer = (tab = 'routes') => { setDrawerTab(tab); setDrawerOpen(true); };
  const closeDrawer = () => setDrawerOpen(false);
  const applyPreset = (preset) => {
    const t = new Date();
    if (preset === 'today') { setStartDate(t); setEndDate(t); }
    if (preset === 'yesterday') { const y = addDays(t, -1); setStartDate(y); setEndDate(y); }
    if (preset === 'week') { setStartDate(startOfWeek(t)); setEndDate(endOfWeek(t)); }
    if (preset === '7days') { setStartDate(addDays(t, -6)); setEndDate(t); }
    if (preset === 'month') { const first = new Date(t.getFullYear(), t.getMonth(), 1); const last = new Date(t.getFullYear(), t.getMonth() + 1, 0); setStartDate(first); setEndDate(last); }
  };
  const toggleExpanded = (routeId) => {
    setExpandedRoutes(prev => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  };
  const handlePersonClick = (name) => {
    setPersonName(name);
    setPersonRole('all');
    setSelectedRouteIds([]); // показать все его появления
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // drawer: фильтрованный список с поиском (реактивный)
  const drawerRouteList = useMemo(() => {
    const q = drawerSearch.trim().toLowerCase();
    let list = routes;

    // покажем, как они проходят статусные фильтры и фильтр по человеку — ориентируемся на текущие глобальные
    if (personName) {
      list = list.filter(r => {
        const asDriver = r.driver === personName;
        const asLoader = [r.loader1, r.loader2].includes(personName);
        if (personRole === 'driver') return asDriver;
        if (personRole === 'loader') return asLoader;
        return asDriver || asLoader;
      });
    }
    if (onlyNotDeparted) list = list.filter(isNotDeparted);
    if (onlyNotFullyComplete) list = list.filter(isNotFullyComplete);

    if (!q) return list;
    return list.filter(r => {
      const routeNo = String(r.route_number ?? '').toLowerCase();
      const driver = String(r.driver ?? '').toLowerCase();
      const loaders = [r.loader1, r.loader2].filter(Boolean).join(' ').toLowerCase();
      return routeNo.includes(q) || driver.includes(q) || loaders.includes(q);
    });
  }, [routes, drawerSearch, personName, personRole, onlyNotDeparted, onlyNotFullyComplete]);

  // свайп-вниз (мобилка)
  const onTouchStart = (e) => { touchStartY.current = e.touches?.[0]?.clientY ?? null; };
  const onTouchMove = (e) => {
    if (touchStartY.current == null) return;
    const cur = e.touches?.[0]?.clientY ?? 0;
    if (cur - touchStartY.current > 60) { closeDrawer(); touchStartY.current = null; }
  };

  // ===== рендер =====
  return (
    <div className={styles.pageRoot}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Детализация маршрутов</h1>
        <div className={styles.headerActions}>
          <button className={styles.primaryBtn} onClick={() => openDrawer('routes')}>Фильтр / Выбор</button>
        </div>
      </div>

      {/* Активные теги */}
      <div className={styles.activeFilters}>
        <span className={styles.filterTag}>
          Период: {toLocalDateStr(startDate)} — {toLocalDateStr(endDate)}
        </span>
        {personName && (
          <span className={styles.filterTag}>
            {personName} • {personRole === 'all' ? 'оба' : personRole === 'driver' ? 'водитель' : 'грузчик'}
            <button className={styles.tagX} onClick={() => setPersonName('')}>×</button>
          </span>
        )}
        {onlyNotDeparted && (
          <span className={styles.filterTag}>
            Не уехавшие
            <button className={styles.tagX} onClick={() => setOnlyNotDeparted(false)}>×</button>
          </span>
        )}
        {onlyNotFullyComplete && (
          <span className={styles.filterTag}>
            Не полностью завершённые
            <button className={styles.tagX} onClick={() => setOnlyNotFullyComplete(false)}>×</button>
          </span>
        )}
        {!!selectedRouteIds.length && (
          <span className={styles.filterTag}>
            Маршрутов: {selectedRouteIds.length}
            <button className={styles.tagX} onClick={() => setSelectedRouteIds([])}>×</button>
          </span>
        )}
      </div>

      {/* KPI */}
      <div className={styles.kpiRow}>
        <div className={styles.kpiBox}><div className={styles.kpiLabel}>Всего</div><div className={styles.kpiValue}>{aggProgress.total}</div></div>
        <div className={styles.kpiBox}><div className={styles.kpiLabel}>В работе</div><div className={styles.kpiValue}>{aggProgress.opened}</div></div>
        <div className={styles.kpiBox}><div className={styles.kpiLabel}>Закрыто</div><div className={styles.kpiValue}>{aggProgress.delivered}</div></div>
        <div className={styles.kpiBox}><div className={styles.kpiLabel}>Не доставлено</div><div className={styles.kpiValue}>{aggProgress.undelivered}</div></div>
      </div>

      <div className={styles.overviewGrid}>
      <TimelineBlock
        routes={effectiveRoutes}
        defaultIncludeProducts={false}
        height={420}
        onRouteClick={(route) => {
          setSelectedRouteIds(prev =>
            prev.length === 1 && prev[0] === route.id ? [] : [route.id]
          );
        }}
        onMarkerClick={(receipt, route) => {
          // при клике по точке — как было
        }}
      />
        <div className={styles.mapCard}>
          <div className={styles.blockHeader}>Карта (заглушка)</div>
          <div className={styles.mapStub}>Тут будет карта с точками квитанций</div>
        </div>
      </div>

{/* Вложенная таблица: маршруты -> квитанции */}
<RoutesNestedTable
  routes={effectiveRoutes}
  expanded={expandedRoutes}           // Set с id (по умолчанию пустой = всё свёрнуто)
  onToggle={toggleExpanded}
  onPersonClick={handlePersonClick}
/>

      {/* ШТОРКА (реактивная) */}
      {drawerOpen && (
        <div className={styles.drawerOverlay} onClick={closeDrawer}>
          <div
            className={styles.drawer}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
          >
            <div className={styles.drawerHeader}>
              <div className={styles.drawerGrabber} />
              <div className={styles.drawerTabs}>
                <button className={`${styles.drawerTab} ${drawerTab === 'routes' ? styles.drawerTabActive : ''}`} onClick={() => setDrawerTab('routes')}>Маршруты</button>
                <button className={`${styles.drawerTab} ${drawerTab === 'person' ? styles.drawerTabActive : ''}`} onClick={() => setDrawerTab('person')}>Человек</button>
              </div>
              <button className={styles.drawerClose} onClick={closeDrawer}>×</button>
            </div>

            {/* даты + пресеты (применяются сразу) */}
            <div className={styles.drawerDates}>
              <div className={styles.quickChips}>
                <button className={styles.chip} onClick={() => applyPreset('today')}>Сегодня</button>
                <button className={styles.chip} onClick={() => applyPreset('yesterday')}>Вчера</button>
                <button className={styles.chip} onClick={() => applyPreset('week')}>Эта неделя</button>
                <button className={styles.chip} onClick={() => applyPreset('7days')}>7 дней</button>
                <button className={styles.chip} onClick={() => applyPreset('month')}>Месяц</button>
              </div>
              <div className={styles.dateInputs}>
                <label>С</label>
                <input
                  type="date"
                  value={toLocalDateStr(startDate)}
                  onChange={e => setStartDate(parseLocalDate(e.target.value))}
                />
                <label>По</label>
                <input
                  type="date"
                  value={toLocalDateStr(endDate)}
                  onChange={e => setEndDate(parseLocalDate(e.target.value))}
                />
              </div>
            </div>

            <div className={styles.drawerBody}>
              {/* статусные чекбоксы (сразу применяются) */}
              <div className={styles.statusFilters}>
                <label className={styles.checkItem}>
                  <input type="checkbox" checked={onlyNotDeparted} onChange={e => setOnlyNotDeparted(e.target.checked)} />
                  <span>Не уехавшие (все Открыта)</span>
                </label>
                <label className={styles.checkItem}>
                  <input type="checkbox" checked={onlyNotFullyComplete} onChange={e => setOnlyNotFullyComplete(e.target.checked)} />
                  <span>Не полностью завершённые (есть Открыта)</span>
                </label>
              </div>

              {drawerTab === 'routes' && (
                <>
                  <div className={styles.searchRow}>
                    <input
                      className={styles.searchInput}
                      placeholder="Поиск: #маршрута, водитель, грузчик…"
                      value={drawerSearch}
                      onChange={e => setDrawerSearch(e.target.value)}
                    />
                    <button className={styles.smallBtn} onClick={() => setSelectedRouteIds(drawerRouteList.map(r => r.id))}>Все</button>
                    <button className={styles.smallBtn} onClick={() => setSelectedRouteIds([])}>Сброс</button>
                  </div>

                  <div className={styles.routesList}>
                    {drawerRouteList.map(r => {
                      const prog = getRouteProgress(r.receipts || []);
                      const checked = selectedRouteIds.includes(r.id);
                      const text = `${prog.delivered}/${prog.total}`;

                      const deliveredPct = prog.total ? (prog.delivered / prog.total) * 100 : 0;
                      const openedPct = prog.total ? (prog.opened / prog.total) * 100 : 0;
                      const undelivPct = prog.total ? (prog.undelivered / prog.total) * 100 : 0;

                      return (
                        <label key={r.id} className={`${styles.routeRow} ${checked ? styles.routeRowChecked : ''}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedRouteIds(prev => e.target.checked ? [...prev, r.id] : prev.filter(x => x !== r.id));
                            }}
                          />
                          <div className={styles.routeRowMain}>
                            <div className={styles.rowTop}>
                              <b>№{r.route_number}</b>
                              <span className={styles.dim}>{r.time_from}-{r.time_to}</span>
                            </div>
                            <div className={styles.rowMid}>
                              <span className={styles.dim}>{r.driver}</span>
                              <span className={styles.dim}>{[r.loader1, r.loader2].filter(Boolean).join(', ')}</span>
                              <span className={styles.dim}>{text}</span>
                            </div>
                            <div className={styles.miniBar}>
                              <div className={styles.miniDelivered} style={{ width: `${deliveredPct}%` }} />
                              <div className={styles.miniOpened} style={{ width: `${openedPct}%` }} />
                              <div className={styles.miniUndelivered} style={{ width: `${undelivPct}%` }} />
                            </div>
                          </div>
                        </label>
                      );
                    })}
                    {!drawerRouteList.length && <div className={styles.muted}>Нет маршрутов</div>}
                  </div>
                </>
              )}

              {drawerTab === 'person' && (
                <>
                  <div className={styles.personForm}>
                    <input
                      className={styles.personInput}
                      list="people-list"
                      placeholder="Начните вводить ФИО…"
                      value={personName}
                      onChange={e => setPersonName(e.target.value)}
                    />
                    <datalist id="people-list">
                      {people.map((p, i) => <option key={i} value={p} />)}
                    </datalist>

                    <select
                      className={styles.personRole}
                      value={personRole}
                      onChange={e => setPersonRole(e.target.value)}
                    >
                      <option value="all">Оба</option>
                      <option value="driver">Как водитель</option>
                      <option value="loader">Как грузчик</option>
                    </select>
                  </div>
                  <div className={styles.muted}>
                    Нажми на имя в таблице, чтобы быстро проставить фильтр по человеку.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
